import { useState } from 'react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { Box, FormControl, InputLabel, Select, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { calculateOptimalZoom, EventMarker, CurvedPolylines, MapController } from './utils';
import { MapLocationEvent } from '../../types';
import EventDetails from '../Common/EventDetails';

const MapView = ({ eventList, orgId }: { eventList: MapLocationEvent[]; orgId: string }) => {
    const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
    const [mapInstance, setMapInstance] = useState<{ panTo: (latLng: { lat: number; lng: number }) => void; setZoom: (zoom: number) => void } | null>(null);
    const [showEventDetails, setShowEventDetails] = useState<boolean>(false);
    const [selectedEventData, setSelectedEventData] = useState<any>(null);

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    // Calculate center of all events
    const center = {
        lat: eventList.reduce((sum, e) => sum + e.latitude, 0) / eventList.length,
        lng: eventList.reduce((sum, e) => sum + e.longitude, 0) / eventList.length,
    };

    // Calculate optimal zoom level based on event distribution
    const optimalZoom = calculateOptimalZoom(eventList);

    const handleMarkerClick = (index: number) => {
        const event = eventList[index];

        if (event.groupedLocations && event.groupedLocations.length > 0) {
            // Pass groupedLocations array to EventDetails
            setSelectedEventData(event.groupedLocations);
        } else if (event.primaryEvent) {
            // Pass single primaryEvent as array
            setSelectedEventData([event.primaryEvent]);
        } else {
            // Fallback: create event data from the marker event itself
            setSelectedEventData([{
                eventID: event.eventId,
                eventDescription: event.event,
                location: event.location,
                latitude: event.latitude,
                longitude: event.longitude,
                eventStartUnixEpoch: event.eventStartUnixEpoch,
            }]);
        }
        setShowEventDetails(true);
    };

    const handleLocationChange = (index: number) => {
        setSelectedLocation(index);
    };

    const handleResetView = () => {
        setSelectedLocation(null);
        if (mapInstance) {
            mapInstance.panTo(center);
            mapInstance.setZoom(optimalZoom);
        }
    };

    return (
        <Box sx={{ width: '100%', position: 'relative' }}>
            {/* Location Dropdown */}
            <Box sx={{
                position: 'absolute',
                top: isDesktop ? 25 : 75,
                left: isDesktop ? '50%' : '3%',
                transform: isDesktop ? 'translateX(-50%)' : 'translateX(0)',
                zIndex: 1000,
                backgroundColor: 'white',
                borderRadius: 1,
                boxShadow: 2,
                minWidth: { xs: '190px', sm: '300px' },
            }}>
                <FormControl fullWidth size="small" >
                    <InputLabel
                        sx={{
                            color: '#000',
                            '&.Mui-focused': {
                                color: '#000',
                            }
                        }}
                    >
                        Select Location
                    </InputLabel>
                    <Select
                        value={selectedLocation ?? ''}
                        label="Select Location"
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === 'reset') {
                                handleResetView();
                            } else {
                                handleLocationChange(Number(value));
                            }
                        }}
                        sx={{ backgroundColor: 'white', color: '#000' }}
                    >
                        <MenuItem value="reset">
                            <em>Show All Locations</em>
                        </MenuItem>
                        {eventList?.map((event, index) => (
                            <MenuItem key={event.eventId || index} value={index}>
                                {event.location}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Map Container */}
            <Box sx={{ width: '100%', height: '500px', mt: 2, borderRadius: 2, overflow: 'hidden' }}>
                <APIProvider apiKey={`${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}>
                    <Map
                        defaultCenter={center}
                        defaultZoom={optimalZoom}
                        mapId="trace-map"
                        gestureHandling={'greedy'}
                        disableDefaultUI={false}
                        mapTypeControl
                        style={{ width: '100%', height: '100%' }}
                    >
                        {/* Map Controller for zoom functionality */}
                        <MapController
                            selectedLocation={selectedLocation}
                            events={eventList}
                            onReady={setMapInstance}
                        />
                        {/* Render curved polylines */}
                        <CurvedPolylines events={eventList} />

                        {/* Render markers */}
                        {eventList?.map((event, index) => {
                            const isLast = index === eventList.length - 1;

                            return (
                                <AdvancedMarker
                                    key={event.eventId || `event-${index}`}
                                    position={{ lat: event.latitude, lng: event.longitude }}
                                    onClick={() => handleMarkerClick(index)}
                                    title={event.event}
                                >
                                    <EventMarker isLast={isLast} />
                                </AdvancedMarker>
                            );
                        })}
                    </Map>
                </APIProvider>
            </Box>

            {/* Event Details Modal */}
            {showEventDetails && (
                <EventDetails
                    open={showEventDetails}
                    orgId={orgId}
                    onClose={() => {
                        setShowEventDetails(false);
                        setSelectedEventData(null);
                    }}
                    eventData={selectedEventData}
                />
            )}
        </Box>
    );
};

export default MapView;